import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import logger from '../utils/logger';
import api from '../services/api';
import * as userService from '../services/userService';
import { User } from '../types/User';

interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  oab?: string;
  estado_oab?: string;
  cpf_cnpj?: string;
  officeAddress?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  loginWithGoogle: (token: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  updateUser: (userData: UpdateUserData) => Promise<User | null>;
  uploadAvatar: (file: File) => Promise<void>;
  deleteAvatar: () => Promise<void>;
  refreshUserData: () => Promise<User | null>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  isAdmin: () => boolean;
  canAccessFlowAdmin: () => boolean;
}

interface RegisterData {
  email: string;
  nome_completo: string;
  cpf_cnpj?: string; // Campo opcional
  numero_oab?: string; // Campo opcional
  estado_oab?: string; // Campo opcional
  phone?: string; // Campo opcional
  password: string;
}

interface TokenData {
  exp: number;
  sub: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Depois do login volta para a página onde a sessão expirou (?next=/caminho), só caminhos internos
const postLoginPath = (): string => {
  const next = new URLSearchParams(window.location.search).get('next') || '';
  return next.startsWith('/') && !next.startsWith('//') ? next : '/dashboard';
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check if token exists and is valid on initial load
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');

      // logger.debug('🔍 [AUTH] Checking authentication on page load:', {
      //   hasToken: !!token,
      //   tokenLength: token?.length,
      //   tokenPreview: token ? token.substring(0, 30) + '...' : 'none',
      //   localStorage: Object.keys(localStorage)
      // });

      if (token) {
        try {
          // Check if token is expired
          const decoded = jwtDecode<TokenData>(token);
          const currentTime = Date.now() / 1000;

          // Add debug logging for token expiration
          // logger.debug('Token validation:', {
          //   tokenExp: decoded.exp,
          //   currentTime: currentTime,
          //   timeUntilExpiry: decoded.exp - currentTime,
          //   isExpired: decoded.exp < currentTime
          // });

          if (decoded.exp < currentTime) {
            // Token expired
            logger.debug('Token expired, clearing auth data');
            localStorage.removeItem('token');
            localStorage.removeItem('userProfile');
            delete api.defaults.headers.common['Authorization'];
            setUser(null);
            setLoading(false);
            return;
          }

          // Note: Authorization header is automatically added by api interceptor
          // But we'll set it here too for redundancy
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          // Get user data with better error handling
          // logger.debug('Attempting to fetch user data with token:', token.substring(0, 20) + '...');
          const response = await api.get('/users/me');
          // logger.debug('Raw API response:', response.data);

          // Use userService to properly map the data
          const mappedUser = userService.mapUserFromApi(response.data);
          // logger.debug('Mapped user data in checkAuth:', mappedUser);

          // Save user profile to localStorage for offline access
          localStorage.setItem('userProfile', JSON.stringify(mappedUser));

          setUser(mappedUser as any);
          // logger.debug('✅ [AUTH] User authenticated successfully on page load');

        } catch (err: any) {
          logger.error('❌ [AUTH] Error validating authentication:', {
            error: err,
            status: err.response?.status,
            message: err.message,
            code: err.code,
            url: err.config?.url
          });

          // Só remover o token se for erro de autenticação (401, 403) ou token inválido
          if (err.response?.status === 401 || err.response?.status === 403 ||
            err.message?.includes('token') || err.name === 'InvalidTokenError') {
            logger.debug('⚠️ [AUTH] Authentication error detected, clearing auth data');
            localStorage.removeItem('token');
            localStorage.removeItem('userProfile');
            delete api.defaults.headers.common['Authorization'];
            setUser(null);
          } else {
            // Para outros erros (network, timeout, etc), tentar recuperar do cache
            // logger.warn('🔄 [AUTH] Network or temporary error, attempting to use cached user profile');

            try {
              const cachedProfile = localStorage.getItem('userProfile');
              if (cachedProfile) {
                const parsedProfile = JSON.parse(cachedProfile);
                // logger.debug('✅ [AUTH] Using cached user profile:', parsedProfile);
                setUser(parsedProfile);
                // Tentar buscar dados atualizados em background
                setTimeout(async () => {
                  try {
                    const response = await api.get('/users/me');
                    const mappedUser = userService.mapUserFromApi(response.data);
                    setUser(mappedUser as any);
                    localStorage.setItem('userProfile', JSON.stringify(mappedUser));
                    // logger.debug('✅ [AUTH] User profile updated from server');
                  } catch (retryErr) {
                    // logger.warn('⚠️ [AUTH] Retry failed, keeping cached profile');
                  }
                }, 2000);
              } else {
                // logger.warn('⚠️ [AUTH] No cached profile available, network error will cause logout');
              }
            } catch (cacheErr) {
              logger.error('❌ [AUTH] Error reading cached profile:', cacheErr);
            }
          }
        }
      } else {
        // logger.debug('❌ [AUTH] No token found in localStorage:', {
        //   localStorageKeys: Object.keys(localStorage),
        //   currentPath: window.location.pathname,
        //   referrer: document.referrer
        // });
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  // Atualizar dados do usuário quando a janela recebe foco (volta de outra aba)
  useEffect(() => {
    const handleWindowFocus = async () => {
      // Só atualizar se o usuário estiver logado
      if (localStorage.getItem('token') && !loading) {
        try {
          // logger.debug('Janela recebeu foco - atualizando dados do usuário');
          await refreshUserData();
          // logger.debug('Dados do usuário atualizados após foco da janela');
        } catch (error) {
          logger.error('Erro ao atualizar dados no foco:', error);
        }
      }
    };

    // Adicionar listener para foco da janela
    window.addEventListener('focus', handleWindowFocus);

    // Cleanup
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [loading]); // Dependência no loading para evitar chamadas durante carregamento inicial

  // Função para atualizar os dados do usuário no contexto
  const refreshUserData = async () => {
    try {
      const response = await api.get('/users/me');
      logger.debug('Raw API response in refreshUserData:', response.data);

      // Use userService to properly map the data
      const mappedUser = userService.mapUserFromApi(response.data);
      logger.debug('Mapped user data in refreshUserData:', mappedUser);

      setUser(mappedUser as any);
      return mappedUser as any;
    } catch (err) {
      logger.error('Error refreshing user data:', err);
      return null;
    }
  };

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      // Use x-www-form-urlencoded format
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);

      const response = await api.post('/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      logger.debug('Login response:', response.data);

      if (!response.data.access_token) {
        throw new Error('Token não recebido do servidor');
      }

      const { access_token } = response.data;

      logger.debug('💾 [AUTH] Saving token to localStorage:', {
        tokenLength: access_token.length,
        tokenPreview: access_token.substring(0, 30) + '...'
      });

      localStorage.setItem('token', access_token);

      // Verify token was saved
      const savedToken = localStorage.getItem('token');
      logger.debug('✅ [AUTH] Token saved verification:', {
        saved: !!savedToken,
        matches: savedToken === access_token
      });

      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      try {
        // Get user data
        const userResponse = await api.get('/users/me');
        logger.debug('User data response:', userResponse.data);

        if (!userResponse.data) {
          throw new Error('Dados do usuário não recebidos');
        }

        // Use userService to properly map the data
        const mappedUser = userService.mapUserFromApi(userResponse.data);
        logger.debug('Mapped user data in login:', mappedUser);

        // Save user profile to localStorage for offline access
        localStorage.setItem('userProfile', JSON.stringify(mappedUser));
        logger.debug('💾 [AUTH] User profile saved to localStorage');

        setUser(mappedUser as any);
        navigate(postLoginPath());
      } catch (userErr: any) {
        logger.error('Error fetching user data:', userErr);
        const errorMessage = userErr.response?.data?.detail || 'Erro ao obter dados do usuário';
        setError(errorMessage);
        localStorage.removeItem('token');
        api.defaults.headers.common['Authorization'] = '';
      }
    } catch (err: any) {
      logger.error('Login error:', err);
      logger.debug('Login error details:', err.response?.data);

      let errorMessage = 'Erro no login. Verifique suas credenciais.';

      if (err.response?.data?.detail) {
        errorMessage = typeof err.response.data.detail === 'string'
          ? err.response.data.detail
          : 'Erro no login. Verifique suas credenciais.';
      } else if (err.message) {
        // Check if it's a network error (backend not running)
        if (err.message.includes('Network Error') || err.code === 'ERR_NETWORK') {
          errorMessage = 'O servidor não está respondendo. Por favor, verifique se o backend está em execução.';
        } else {
          errorMessage = err.message;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      setLoading(true);
      setError(null);

      // Dividir nome completo em primeiro e último nome
      const nomePartes = userData.nome_completo.trim().split(' ');
      const firstName = nomePartes[0] || '';
      const lastName = nomePartes.slice(1).join(' ') || '';

      // Dados básicos para registro
      const requestData = {
        email: userData.email,
        first_name: firstName,
        last_name: lastName,
        cpf_cnpj: userData.cpf_cnpj,
        numero_oab: userData.numero_oab,
        estado_oab: userData.estado_oab,
        phone: userData.phone,
        password: userData.password,
      };

      logger.debug('Dados para registro:', JSON.stringify(requestData, null, 2));

      const response = await api.post('/auth/register', requestData);

      logger.debug('Registro bem-sucedido:', response.status);

      // Pequena pausa para garantir que o usuário foi criado no backend
      await new Promise(resolve => setTimeout(resolve, 1000));

      try {
        // Auto login after registration
        const loginFormData = new URLSearchParams();
        loginFormData.append('username', userData.email);
        loginFormData.append('password', userData.password);

        const loginResponse = await api.post('/auth/login', loginFormData, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        });

        const { access_token } = loginResponse.data;

        localStorage.setItem('token', access_token);
        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

        // Get user data
        const userResponse = await api.get('/users/me');
        const mappedUser = userService.mapUserFromApi(userResponse.data);
        setUser(mappedUser as any);

        // Redirecionar para onde o usuário estava (sessão expirada) ou para o dashboard
        navigate(postLoginPath());

        return response.data;
      } catch (loginErr) {
        logger.error('Erro no login após registro:', loginErr);
        // Não mostra erro de login aqui, apenas retorna os dados do registro
        // O usuário verá o alerta de sucesso do registro
        return response.data;
      }
    } catch (err: any) {
      logger.error('Erro no registro:', err);

      // Log de detalhes do erro
      if (err.response) {
        logger.debug('Detalhes do erro:', {
          status: err.response.status,
          data: JSON.stringify(err.response.data, null, 2)
        });

        // Verificar conflitos (409) - email ou CPF/CNPJ duplicado
        if (err.response.status === 409) {
          const errorDetail = err.response.data?.detail || 'Dados já cadastrados no sistema.';
          setError(errorDetail);
          setLoading(false);
          throw err;
        }

        // Verificar erro de validação (422)
        if (err.response.status === 422) {
          const validationErrors = err.response.data?.detail;
          if (Array.isArray(validationErrors)) {
            // Pegar a primeira mensagem de erro de validação
            const firstError = validationErrors[0];
            const errorMsg = firstError?.msg || firstError?.message || 'Dados inválidos fornecidos.';
            setError(`Erro de validação: ${errorMsg}`);
          } else if (typeof validationErrors === 'string') {
            setError(validationErrors);
          } else {
            setError('Dados inválidos fornecidos. Verifique os campos e tente novamente.');
          }
          setLoading(false);
          throw err;
        }
      }

      // Erro genérico
      const errorMessage = typeof err.response?.data?.detail === 'object'
        ? 'Erro no cadastro. Tente novamente.'
        : err.response?.data?.detail || 'Erro no cadastro. Tente novamente.';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    navigate('/login');
  };

  const clearError = () => {
    setError(null);
  };

  const updateUser = async (userData: UpdateUserData) => {
    try {
      setLoading(true);
      setError(null);

      logger.debug('AuthContext - Updating user with data:', userData);

      // Usar o serviço para atualizar os dados do usuário
      const updatedUser = await userService.updateUserProfile(userData);

      logger.debug('AuthContext - Received updated user from API:', updatedUser);

      // Atualizar o estado do usuário, preservando os dados existentes
      setUser(prevUser => {
        if (!prevUser) return updatedUser as any;
        const mergedUser = { ...prevUser, ...updatedUser };
        logger.debug('AuthContext - Merged user state:', mergedUser);
        return mergedUser;
      });

      // Retornar o usuário atualizado para atualizar o formulário
      return updatedUser as any;
    } catch (err: any) {
      logger.error('Update user error:', err);
      setError(err.response?.data?.detail || 'Erro ao atualizar perfil. Tente novamente.');
      return Promise.reject(err);
    } finally {
      setLoading(false);
    }
  };

  const uploadAvatar = async (file: File) => {
    try {
      setLoading(true);
      setError(null);

      // Usar o serviço para fazer upload do avatar
      await userService.uploadAvatar(file);

      // Forçar refresh dos dados do usuário para garantir que o avatar seja atualizado
      await refreshUserData();

      return Promise.resolve();
    } catch (err: any) {
      logger.error('Upload avatar error:', err);
      setError(err.response?.data?.detail || 'Erro ao fazer upload da imagem. Tente novamente.');
      return Promise.reject(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteAvatar = async () => {
    try {
      setLoading(true);
      setError(null);

      // Usar o serviço para excluir o avatar
      await userService.deleteAvatar();

      // Forçar refresh dos dados do usuário para garantir que o avatar seja removido
      await refreshUserData();

      return Promise.resolve();
    } catch (err: any) {
      logger.error('Delete avatar error:', err);
      setError(err.response?.data?.detail || 'Erro ao remover a imagem. Tente novamente.');
      return Promise.reject(err);
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async (token: string) => {
    try {
      setLoading(true);
      setError(null);

      // Chamar o endpoint de login com Google no backend
      const response = await api.post('/auth/google', { token }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const { access_token } = response.data;

      localStorage.setItem('token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      try {
        // Obter dados do usuário
        const userResponse = await api.get('/users/me');
        const mappedUser = userService.mapUserFromApi(userResponse.data);

        // Save user profile to localStorage for offline access
        localStorage.setItem('userProfile', JSON.stringify(mappedUser));

        setUser(mappedUser as any);

        // Verificar se é um novo usuário que precisa completar cadastro
        if (response.data.is_new_user) {
          // Redirecionar para página de cadastro com dados do Google
          navigate('/register', {
            state: {
              socialUser: {
                email: mappedUser.email,
                firstName: mappedUser.firstName,
                lastName: mappedUser.lastName,
                provider: 'google'
              }
            }
          });
        } else {
          navigate('/dashboard');
        }
      } catch (userErr: any) {
        logger.error('Erro ao obter dados do usuário Google:', userErr);
        const errorMessage = typeof userErr.response?.data?.detail === 'object'
          ? 'Erro ao obter dados do usuário'
          : userErr.response?.data?.detail || 'Erro ao obter dados do usuário';
        setError(errorMessage);
        localStorage.removeItem('token');
      }
    } catch (err: any) {
      logger.error('Erro no login com Google:', err);
      const errorMessage = typeof err.response?.data?.detail === 'object'
        ? 'Erro no login com Google. Tente novamente.'
        : err.response?.data?.detail || 'Erro no login com Google. Tente novamente.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };




  const updatePassword = async (currentPassword: string, newPassword: string) => {
    try {
      setLoading(true);
      setError(null);

      // Usar o serviço para atualizar a senha
      await userService.updatePassword(currentPassword, newPassword);

      return Promise.resolve();
    } catch (err: any) {
      logger.error('Update password error:', err);

      let errorMessage = 'Erro ao atualizar a senha.';

      if (err.response?.status === 401) {
        errorMessage = 'A senha atual está incorreta.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }

      setError(errorMessage);
      return Promise.reject(err);
    } finally {
      setLoading(false);
    }
  };

  // Verifica se o usuário atual é administrador
  const isAdmin = () => {
    if (!user) return false;
    return user.role === 'admin' || user.is_admin === true;
  };

  const canAccessFlowAdmin = () => {
    if (!user) return false;
    return isAdmin() || user.role === 'flow_admin';
  };

  const value = {
    user,
    isAuthenticated: !!user,
    loading,
    error,
    login,
    register,
    loginWithGoogle,
    logout,
    clearError,
    updateUser,
    uploadAvatar,
    deleteAvatar,
    refreshUserData,
    updatePassword,
    isAdmin,
    canAccessFlowAdmin
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 